import React, { useEffect, useContext, useState, useLayoutEffect } from 'react';
import _round from 'lodash/round';
import { useHistory } from 'react-router-dom';
import { useFormikContext } from 'formik';
import { createStyles } from 'utils/createStyle';
import { Footer } from './Footer';
import Header from './Header';
import BulkOrderForm from './BulkOrderForm';
import BulkOrderLoading from './BulkOrderLoading';
import { BulkOrderContext, BulkOrderProvider } from './BulkOrderContext';
import Modal2 from './Modal';
import { FadeIn } from 'components/loading-screen/FadeIn';
import { Faceplate } from 'common/api/misc';
import { ProductAmountInputFormData } from 'features/product/components/ProductAmountInput';
import { useModalControls, ModalControls } from 'hooks/useModalControls';
import { RecipientListModal } from './RecipientListModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { Product } from 'common/api/e-comm/models/Product';
import { Btn } from 'components/btn/Btn';
import { Modal } from 'components/modal/Modal';
import { useFirebaseConfigValues, firebaseValueAsNumber } from 'hooks/useFirebaseConfigValues';

// temporary devcode
import mockCardDesigns from './brand-images.fixture';
import { useRootSelector } from 'rootStore';
import { selectIsBulkEgiftingEnabled } from 'common/features/store/duck/ffs';

const styles = createStyles({
    wrapper: 'overflow-y-scroll w-full mx-auto bg-white absolute top-0 bottom-0 left-0 right-0',
    body: ['flex flex-col sm:flex-row flex-grow min-h-0'],
    leftPreview: ['w-full sm:w-1/3 md:w-2/5 flex items-center justify-center bg-grey-accent'],
    cardPreview: [
        'w-full h-full object-cover mb-4 rounded-xl',
        { width: '346px', height: '230px' },
    ],
    rightSteppingForm: ['w-full sm:w-2/3 md:w-3/5', { minHeight: 950 }],
    formContainer: ['flex flex-col sm:h-auto p-4 mx-auto w-max sm:p-0', { minHeight: '548px' }],
    formStepper: ['text-center text-grey-medium', { marginTop: 165 }],
    footer: ['w-full text-white', { height: '100px', background: '#143860' }],
    recipientListScrollContent: [
        'grid overflow-y-scroll',
        {
            gridColumn: '1 / -1',
            maxHeight: '50vh',
            gridTemplateColumns: 'auto 1fr 1fr',
            scrollbarWidth: 'thin' /* For Firefox */,
            scrollbarColor: 'transparent transparent' /* For Firefox */,
            '::-webkit-scrollbar': {
                width: '8px' /* For Chrome, Safari, and Opera */,
            },
            '::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0, 0, 0, 0.2)' /* For Chrome, Safari, and Opera */,
                borderRadius: '20px',
            },
            '::-webkit-scrollbar-track': {
                backgroundColor: 'transparent' /* For Chrome, Safari, and Opera */,
            },
        },
    ],
    recipientListContainer: [
        'border rounded ounded-lg border-grey-line mt-8 mx-auto',
        { boxShadow: '0px 18px 32px rgba(208, 210, 218, 0.15)' },
    ],
    recipientListData: ['grid text-align-left', { gridTemplateColumns: 'auto 1fr 1fr' }],
    recipientListFooter: [
        '',
        {
            boxShadow:
                '0px 0px 16px -4px rgba(16, 24, 40, 0.08), 0px -2px 6px -2px rgba(16, 24, 40, 0.03)',
        },
    ],
    info: [
        'text-brand text-sm font-semibold border-l-4 border-primary-blue-light px-5 py-4 outline-none focus:outline-none',
        { boxShadow: '0px 8px 32px rgba(44, 45, 46, 0.12)', width: 335 },
    ],
});

type BulkOrderScreenProps = {
    facePlates: Faceplate[];
    brandName: string;
    brandImageUrl: string;
    product: Product;
    closeGiftDetails: () => void;
};

const getAmount = (selectedOption: { [key: string]: string }, priceOverride: number) => {
    let amount = '$0.00';
    const selectedOption_ = Object.values(selectedOption);
    if (priceOverride) {
        amount = `$${_round(priceOverride, 2).toFixed(2)}`;
    } else if (selectedOption_.length > 0) {
        amount = selectedOption_[0];
    }
    return amount;
};

const useRemoveDocumentBodyScroll = () => {
    useLayoutEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = '';
        };
    }, []);
};

export const BulkOrderScreen = ({
    brandImageUrl,
    brandName,
    facePlates,
    product,
}: Omit<BulkOrderScreenProps, 'closeGiftDetails'>) => {
    const { state, setGiftImageOptions, setSelectedCardDesign, setStep, setProductId } =
        useContext(BulkOrderContext);
    const history = useHistory();
    const [modalVisible, setModalVisible] = useState(false);

    const formikCtx = useFormikContext<ProductAmountInputFormData>();
    const amount = getAmount(formikCtx.values.selectedOptions, formikCtx.values.priceOverride || 0);
    const [exitLocation, setExitLocation] = useState<string | undefined>();
    const enable_bulk_egifting = useRootSelector(selectIsBulkEgiftingEnabled);

    useRemoveDocumentBodyScroll();

    // modals
    const recipientsListModal = useModalControls();
    const recipientsInfoModal = useModalControls();

    useEffect(() => {
        setProductId(Number(product.id));
    }, [product, product.id]);

    useEffect(() => {
        const designs = facePlates || mockCardDesigns;

        // NOTE: We skip step 1 and automagically select the first card design.
        if (designs.length === 1) {
            setStep(2);
        }
        setGiftImageOptions(designs);
        setSelectedCardDesign(designs[0]);
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (!enable_bulk_egifting) {
            history.goBack();
        }
    }, [enable_bulk_egifting, history]);

    const toggleModal = () => {
        setModalVisible(!modalVisible);
    };

    const handleExit = (location?: string) => {
        const earlyExitAllowed =
            (state.step === 1 && state.giftImageOptions.length > 1) ||
            (state.step === 2 && state.giftImageOptions.length === 1);
        if (earlyExitAllowed) {
            if (location) {
                history.replace(location);
            } else {
                history.goBack();
            }
        } else {
            toggleModal();
            setExitLocation(location);
        }
    };

    if (state.addingToCartLoading) {
        return <BulkOrderLoading />;
    } else if (!enable_bulk_egifting) {
        return null;
    }

    return (
        <div className={styles.wrapper}>
            <Header onExit={handleExit} />

            <FadeIn when className={styles.body}>
                {state.step !== 5 && (
                    <div className={styles.leftPreview}>
                        <div className="flex flex-col items-center justify-center mx-4">
                            <img
                                src={state.selectedCardDesign?.ImageUrl}
                                alt="Selected card design"
                                className={styles.cardPreview}
                            />
                            <div className="flex items-center w-full">
                                <div className="bg-white p-1 mr-2 rounded-lg">
                                    <img
                                        className="object-contain w-10 h-10"
                                        src={brandImageUrl}
                                        alt="Logo"
                                    />
                                </div>
                                <span className="flex-grow text-left font-weight-600 text-sm text-grey-2">
                                    {brandName}
                                </span>
                                <span className="text-right font-semibold text-xl text-brand-dark">
                                    {amount}
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                <div
                    className={
                        state.step !== 5 ? styles.rightSteppingForm : 'w-full flex justify-center'
                    }
                >
                    <div className={styles.formContainer}>
                        {state.step !== 5 && (
                            <div className={styles.formStepper}>
                                Step{' '}
                                {state.giftImageOptions.length === 1 ? state.step - 1 : state.step}/
                                {state.giftImageOptions.length === 1 ? 3 : 4}
                            </div>
                        )}

                        <BulkOrderForm
                            amount={amount}
                            brandName={brandName}
                            onCancel={handleExit}
                            product={product}
                            recipientsListModal={recipientsListModal}
                        />
                    </div>
                    {state.step === 4 && (
                        <div className="ml-5">
                            <button
                                type="button"
                                className={styles.info}
                                onClick={recipientsInfoModal.toggleModalVisibility}
                            >
                                What if I have more than 125 recipients?
                            </button>
                        </div>
                    )}
                </div>
            </FadeIn>
            <div className={styles.footer}>
                <Footer />
            </div>

            <NumberRecipientsInfoModal modalControls={recipientsInfoModal} />

            {modalVisible && (
                <Modal2 onClose={toggleModal}>
                    <div className="text-center mt-8 mb-8">
                        <FontAwesomeIcon
                            icon={faExclamationTriangle}
                            className="text-error text-4xl"
                        />

                        <h2 className="text-29px text-brand-dark font-semibold mt-6 mb-5">
                            Are you sure?
                        </h2>

                        <p className="text-grey-medium text-center">
                            By exiting this window, all of the order progress you've
                        </p>
                        <p className="mb-8 text-grey-medium text-center">
                            made so far will not be saved.
                        </p>

                        <div className="text-center">
                            <div className="mb-4">
                                <button
                                    className="h-40px bg-brand hover:bg-brand-dark text-white font-semibold px-8 rounded-full"
                                    onClick={() => setModalVisible(false)}
                                >
                                    Continue Editing
                                </button>
                            </div>

                            <div>
                                <button
                                    className="bg-white text-brand font-semibold text-base leading-7 pt-2 px-8 rounded-full inline-flex items-center justify-center focus:outline-none underline"
                                    onClick={() => {
                                        if (exitLocation) {
                                            history.replace(exitLocation);
                                        } else {
                                            history.goBack();
                                        }
                                    }}
                                >
                                    Yes, I want to leave
                                </button>
                            </div>
                        </div>
                    </div>
                </Modal2>
            )}

            <RecipientListModal
                fileName={state.fileDetails?.name}
                recipients={state.recipients}
                modalControls={recipientsListModal}
            />
        </div>
    );
};

const NumberRecipientsInfoModal = ({ modalControls }: { modalControls: ModalControls }) => {
    const max_cart_line_items = useFirebaseConfigValues(
        'max_cart_line_items',
        0,
        firebaseValueAsNumber
    );
    return (
        <Modal
            showCloseButton
            title=""
            isOpen={modalControls.isVisible}
            onClose={modalControls.toggleModalVisibility}
        >
            <h1 className="text-29px text-center text-brand-dark font-semibold">
                {max_cart_line_items}+ Gifts
            </h1>
            <p className="text-center text-grey-medium mt-2">
                You can send gifts to more than {max_cart_line_items} recipients by placing multiple
                orders.
            </p>
            <Btn className="mt-5 w-32 mx-auto" onClick={modalControls.toggleModalVisibility}>
                OK
            </Btn>
        </Modal>
    );
};

const WrappedBulkOrderScreen = ({ closeGiftDetails, ...props }: BulkOrderScreenProps) => {
    const modal = useModalControls();
    return (
        <BulkOrderProvider errorModalControls={modal} closeGiftDetails={closeGiftDetails}>
            <BulkOrderScreen {...props} />
            <BulkGiftingErrorModal modalControls={modal} />
        </BulkOrderProvider>
    );
};

const BulkGiftingErrorModal = ({ modalControls }: { modalControls: ModalControls }) => {
    const history = useHistory();
    const closeModal = () => {
        modalControls.toggleModalVisibility();
        history.goBack();
    };

    return (
        <Modal showCloseButton title="" isOpen={modalControls.isVisible} onClose={closeModal}>
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-error text-4xl mx-auto" />
            <h1 className="text-29px text-center text-brand-dark font-semibold mt-5 mb-4">
                Unexpected Error
            </h1>
            <p className="text-center text-grey-medium mt-2 text-17px">
                Something went wrong while adding the items to your cart. Please call Customer
                Support at 800-727-4715 (option 3) for assistance.
            </p>
            <Btn className="mt-5 w-48 mx-auto" onClick={closeModal}>
                Ok
            </Btn>
        </Modal>
    );
};

export default WrappedBulkOrderScreen;
